import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import { FaTrashAlt, FaEdit } from 'react-icons/fa';
import { MdFullscreen, MdFullscreenExit, MdSync } from 'react-icons/md';
import { deleteNote as _deleteNote } from 'redux/crm/project/projectActivitySlice';
import NoteEditor from './NoteEditor';

const sanitize = (html) => DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } });

const fmtDateTime = (dt) => {
  const d = new Date(dt);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const NoteCard = ({
  note, projectId,
  deleteAction = _deleteNote,
  createAction, updateAction, submittingSelector,
}) => {
  const dispatch = useDispatch();
  const [editing,    setEditing]    = useState(false);
  const [expanded,   setExpanded]   = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Read from the new permissions object; fall back to legacy flat fields for
  // backwards compatibility with cached/older API responses.
  const canEdit   = note.permissions?.allow_edit   ?? note.can_edit   ?? true;
  const canDelete = note.permissions?.allow_delete  ?? note.can_delete ?? true;

  const handleDelete = () => {
    confirmAlert({
      title:   'Delete note',
      message: 'Are you sure you want to delete this note? This action <strong>cannot be undone</strong>.',
      type:    'danger',
      closeOnClickOutside: true,
      closeOnEscape:       true,
      buttons: [
        {
          label: 'Delete',
          onClick: async () => {
            const res = await dispatch(deleteAction({ projectId, id: note.id }));
            if (res.meta.requestStatus !== 'fulfilled') toast.error('Failed to delete note.');
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  };

  const openFullscreen = () => { setFullscreen(true);  setExpanded(true); };

  const handleCloseExpand = () => {
    setExpanded(false);
    setFullscreen(false);
  };

  useEffect(() => {
    if (!expanded) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  if (editing) {
    return <NoteEditor projectId={projectId} record={note} onClose={() => setEditing(false)}
      createAction={createAction} updateAction={updateAction} submittingSelector={submittingSelector} />;
  }

  const metaLine = `${fmtDateTime(note.created_at)}${note.created_by ? ` · ${note.created_by}` : ''}`;

  return (
    <>
      <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm tw-overflow-hidden tw-transition-shadow hover:tw-shadow-md">
        {/* Accent strip */}
        <div className="tw-h-0.5 tw-bg-gradient-to-r tw-from-amber-400 tw-to-amber-200" />

        {/* Content preview */}
        <div className="tw-relative">
          <div
            className="tw-p-3.5 tw-text-sm tw-prose tw-prose-sm tw-max-w-none tw-max-h-36 tw-overflow-hidden tw-leading-relaxed"
            dangerouslySetInnerHTML={{ __html: sanitize(note.content) }}
          />
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-10 tw-bg-gradient-to-t tw-from-white tw-to-transparent tw-pointer-events-none" />
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-3.5 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/60">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-1 tw-mr-2 tw-min-w-0">
            <span className="tw-text-[10px] tw-text-blue-500 tw-font-medium tw-truncate">
              {metaLine}
            </span>
            {note.synced_from_opla && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-teal-50 tw-text-teal-600 tw-border tw-border-teal-200 tw-flex-shrink-0" title="Synced from Opla CRM">
                <MdSync size={10} /> Opla
              </span>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0">
            <button type="button" title="Fullscreen" onClick={openFullscreen}
              className="tw-p-1.5 tw-rounded tw-text-slate-400 hover:tw-bg-blue-50 hover:tw-text-blue-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
              <MdFullscreen size={14} />
            </button>
            {canEdit && (
              <button type="button" title="Edit" onClick={() => setEditing(true)}
                className="tw-p-1.5 tw-rounded tw-text-slate-400 hover:tw-bg-blue-50 hover:tw-text-blue-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
                <FaEdit size={11} />
              </button>
            )}
            {canDelete && (
              <button type="button" title="Delete" onClick={handleDelete}
                className="tw-p-1.5 tw-rounded tw-text-slate-400 hover:tw-bg-red-50 hover:tw-text-red-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
                <FaTrashAlt size={10} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded / fullscreen view */}
      {expanded && (
        <div className="tw-fixed tw-inset-0 tw-z-[99999] tw-bg-slate-950/55 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-3 sm:tw-p-4">
          <div className={`tw-relative tw-z-[100000] tw-flex tw-flex-col tw-w-full tw-bg-white tw-shadow-2xl tw-overflow-hidden tw-animate-fade-in ${fullscreen ? 'tw-h-full tw-rounded-none' : 'tw-max-w-5xl tw-max-h-[90vh] tw-rounded-2xl'}`}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-b tw-border-slate-200 tw-bg-slate-50">
              <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate">Note</div>
                <div className="tw-text-[11px] tw-text-slate-500 tw-truncate">{metaLine}</div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setFullscreen(f => !f)}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded tw-border tw-border-slate-200 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-bg-white tw-cursor-pointer tw-transition-colors"
                >
                  {fullscreen ? <MdFullscreenExit size={15} /> : <MdFullscreen size={15} />}
                  {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseExpand}
                  className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-border tw-border-slate-200 tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-bg-white tw-cursor-pointer tw-transition-colors"
                  aria-label="Close note"
                >
                  <span className="tw-text-lg tw-leading-none">×</span>
                </button>
              </div>
            </div>

            <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-p-4 sm:tw-p-6">
              <div className="tw-prose tw-prose-base tw-max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: sanitize(note.content) }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

NoteCard.propTypes = {
  note:               PropTypes.object.isRequired,
  projectId:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  deleteAction:       PropTypes.func,
  createAction:       PropTypes.func,
  updateAction:       PropTypes.func,
  submittingSelector: PropTypes.func,
};

export default NoteCard;
